import { supabaseAdmin as supabase } from '../../../lib/supabase.js';

class TeacherDetailsService {
  // Get all teacher assignment data
  async getTeacherAssignments(tenantId = '1acd60af-8884-48bb-b1ea-13fbb935d3a8') {
    try {
      const { data, error } = await supabase
        .from('saas_myclass_class_subject_item_load_quota_details_teachers')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('subject_code')
        .order('class_code');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching teacher assignments:', error);
      return [];
    }
  }

  // Get teacher assignments with sorting
  async getTeacherAssignmentsSorted(sortBy = 'subject', tenantId = '1acd60af-8884-48bb-b1ea-13fbb935d3a8') {
    try {
      let orderBy = 'subject_code';
      
      switch (sortBy) {
        case 'class':
          orderBy = 'class_code';
          break;
        case 'teacher':
          orderBy = 'teacher_name';
          break;
        default:
          orderBy = 'subject_code';
      }

      const { data, error } = await supabase
        .from('saas_myclass_class_subject_item_load_quota_details_teachers')
        .select('*')
        .eq('tenant_id', tenantId)
        .order(orderBy);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching sorted teacher assignments:', error);
      return [];
    }
  }

  // Get available teachers
  async getAvailableTeachers(tenantId = '1acd60af-8884-48bb-b1ea-13fbb935d3a8') {
    try {
      const { data, error } = await supabase
        .from('saas_teachers')
        .select('id, name, teacher_id, english_name, subject_specialty, status')
        .eq('tenant_id', tenantId)
        .eq('status', 'active')
        .order('name');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching teachers:', error);
      return [];
    }
  }

  // Get teacher load summary
  async getTeacherLoadSummary(tenantId = '1acd60af-8884-48bb-b1ea-13fbb935d3a8') {
    try {
      const { data, error } = await supabase
        .from('saas_myclass_class_subject_item_load_quota_details_teachers')
        .select('teacher_id, teacher_name, teacher_initials, quota_sessions')
        .eq('tenant_id', tenantId);

      if (error) throw error;

      // Calculate total load for each teacher
      const teacherLoads = {};
      data.forEach(assignment => {
        const teacherId = assignment.teacher_id;
        if (!teacherLoads[teacherId]) {
          teacherLoads[teacherId] = {
            teacher_id: teacherId,
            teacher_name: assignment.teacher_name,
            teacher_initials: assignment.teacher_initials,
            total_load: 0,
            assignments: []
          };
        }
        teacherLoads[teacherId].total_load += assignment.quota_sessions || 0;
        teacherLoads[teacherId].assignments.push(assignment);
      });

      return Object.values(teacherLoads);
    } catch (error) {
      console.error('Error calculating teacher loads:', error);
      return [];
    }
  }

  // Assign teacher to class-subject combination
  async assignTeacher(subjectCode, classCode, teacherId, pageNumber = 0, tenantId = '1acd60af-8884-48bb-b1ea-13fbb935d3a8') {
    try {
      // First get teacher details
      const { data: teacherData, error: teacherError } = await supabase
        .from('saas_teachers')
        .select('name, english_name')
        .eq('id', teacherId)
        .eq('tenant_id', tenantId)
        .single();

      if (teacherError) throw teacherError;

      // Get existing assignment or create new one
      const { data: existingData, error: existingError } = await supabase
        .from('saas_myclass_class_subject_item_load_quota_details_teachers')
        .select('*')
        .eq('tenant_id', tenantId)
        .eq('subject_code', subjectCode)
        .eq('class_code', classCode)
        .eq('page_number', pageNumber)
        .single();

      const teacherInitials = this.generateTeacherInitials(teacherData.name, teacherData.english_name);
      const quotaValue = `${subjectCode}--${classCode}--Page ${pageNumber}--${existingData?.quota_sessions || 0}--${teacherInitials}`;

      if (existingData) {
        // Update existing assignment
        const { data, error } = await supabase
          .from('saas_myclass_class_subject_item_load_quota_details_teachers')
          .update({
            teacher_id: teacherId,
            teacher_name: teacherData.name,
            teacher_initials: teacherInitials,
            quota_value: quotaValue,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingData.id)
          .select();

        if (error) throw error;
        return data[0];
      } else {
        // Create new assignment
        const { data, error } = await supabase
          .from('saas_myclass_class_subject_item_load_quota_details_teachers')
          .insert([{
            tenant_id: tenantId,
            subject_code: subjectCode,
            class_code: classCode,
            subject_name: this.getSubjectName(subjectCode),
            class_name: classCode,
            page_number: pageNumber,
            quota_value: quotaValue,
            teacher_id: teacherId,
            teacher_name: teacherData.name,
            teacher_initials: teacherInitials,
            teacher_load: 0,
            quota_sessions: 0,
            quota_group: 'd',
            quota_type: 'normal',
            academic_year: '2024-2025',
            semester: '1',
            status: 'active'
          }])
          .select();

        if (error) throw error;
        return data[0];
      }
    } catch (error) {
      console.error('Error assigning teacher:', error);
      throw error;
    }
  }

  // Remove teacher assignment
  async removeTeacherAssignment(assignmentId, tenantId = '1acd60af-8884-48bb-b1ea-13fbb935d3a8') {
    try {
      const { error } = await supabase
        .from('saas_myclass_class_subject_item_load_quota_details_teachers')
        .delete()
        .eq('id', assignmentId)
        .eq('tenant_id', tenantId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error removing teacher assignment:', error);
      throw error;
    }
  }

  // Update teacher assignment
  async updateTeacherAssignment(assignmentId, updates, tenantId = '1acd60af-8884-48bb-b1ea-13fbb935d3a8') {
    try {
      const { data, error } = await supabase
        .from('saas_myclass_class_subject_item_load_quota_details_teachers')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', assignmentId)
        .eq('tenant_id', tenantId)
        .select();

      if (error) throw error;
      return data[0];
    } catch (error) {
      console.error('Error updating teacher assignment:', error);
      throw error;
    }
  }

  // Search teacher assignments
  async searchTeacherAssignments(searchTerm, tenantId = '1acd60af-8884-48bb-b1ea-13fbb935d3a8') {
    try {
      const { data, error } = await supabase
        .from('saas_myclass_class_subject_item_load_quota_details_teachers')
        .select('*')
        .eq('tenant_id', tenantId)
        .or(`subject_code.ilike.%${searchTerm}%,class_code.ilike.%${searchTerm}%,teacher_name.ilike.%${searchTerm}%,teacher_initials.ilike.%${searchTerm}%`)
        .order('subject_code')
        .order('class_code');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error searching teacher assignments:', error);
      return [];
    }
  }

  // Generate teacher initials from name
  generateTeacherInitials(chineseName, englishName) {
    if (englishName) {
      // Use English name initials
      const parts = englishName.split(' ');
      return parts.map(part => part.charAt(0).toUpperCase()).join('');
    } else if (chineseName) {
      // Use Chinese name initials (first character of each character)
      return chineseName.split('').map(char => char.charAt(0)).join('').substring(0, 3);
    }
    return 'UNK';
  }

  // Get subject name from code
  getSubjectName(subjectCode) {
    const subjectNames = {
      'A&D': 'Art & Design',
      'AMA': 'Applied Mathematics',
      'BIA': 'Business Intelligence',
      'BIO': 'Biology',
      'CCP': 'Computer Programming',
      'CH': 'Chemistry',
      'CHA': 'Chinese Art',
      'CHE': 'Chinese History',
      'CHI': 'Chinese',
      'RCH': 'Chinese Tutoring',
      'CL&': 'Class Leadership',
      'CMA': 'Computer Mathematics',
      'CS': 'Computer Science',
      'CSA': 'Computer Science Advanced',
      'D&T': 'Design & Technology',
      'ECP': 'Economics',
      'ENG': 'English',
      'MATH': 'Mathematics',
      'PHY': 'Physics',
      'CHEM': 'Chemistry'
    };
    return subjectNames[subjectCode] || subjectCode;
  }

  // Get statistics
  async getStatistics(tenantId = '1acd60af-8884-48bb-b1ea-13fbb935d3a8') {
    try {
      const assignments = await this.getTeacherAssignments(tenantId);
      const teacherLoads = await this.getTeacherLoadSummary(tenantId);
      
      const stats = {
        totalAssignments: assignments.length,
        totalTeachers: teacherLoads.length,
        totalSessions: assignments.reduce((sum, item) => sum + (item.quota_sessions || 0), 0),
        unassignedCount: assignments.filter(item => !item.teacher_id).length,
        bySubject: {},
        byTeacher: {}
      };

      assignments.forEach(assignment => {
        const subject = assignment.subject_code;
        const teacher = assignment.teacher_name;
        
        if (!stats.bySubject[subject]) {
          stats.bySubject[subject] = { count: 0, totalSessions: 0 };
        }
        stats.bySubject[subject].count++;
        stats.bySubject[subject].totalSessions += assignment.quota_sessions || 0;
        
        if (teacher) {
          if (!stats.byTeacher[teacher]) {
            stats.byTeacher[teacher] = { count: 0, totalSessions: 0 };
          }
          stats.byTeacher[teacher].count++;
          stats.byTeacher[teacher].totalSessions += assignment.quota_sessions || 0;
        }
      });

      return stats;
    } catch (error) {
      console.error('Error getting statistics:', error);
      return {
        totalAssignments: 0,
        totalTeachers: 0,
        totalSessions: 0,
        unassignedCount: 0,
        bySubject: {},
        byTeacher: {}
      };
    }
  }
}

export default new TeacherDetailsService();
